const hex = (values) => values;
/**
 * The chart colours, light and dark, in their validated order.
 *
 * Checked with a colour-vision simulation: every adjacent pair differs by ΔE ≥ 8 (OKLab × 100) for
 * protan, deutan and tritan vision and ≥ 15 for normal vision, in both columns. Three light steps
 * sit under 3:1 on white, which is why charts always carry labels or a legend beside the colour.
 */
export const CHART_PALETTE = {
    light: hex([
        '#2a78d6',
        '#eb6834',
        '#1baf7a',
        '#eda100',
        '#e87ba4',
        '#008300',
        '#4a3aa7',
        '#e34948',
    ]),
    dark: hex([
        '#3987e5',
        '#d95926',
        '#199e70',
        '#c98500',
        '#d55181',
        '#008300',
        '#9085e9',
        '#e66767',
    ]),
    neutral: { light: '#a3a29c', dark: '#6e6d68' },
    /** Up and down, for a KPI's change. Always shown with an arrow, never by colour alone. */
    good: '#0ca30c',
    bad: '#d03b3b',
};
export function isSeries(paint) {
    return typeof paint === 'object' && 'series' in paint;
}
export function isWash(paint) {
    return typeof paint === 'object' && 'wash' in paint;
}
export function seriesColor(index, dark) {
    if (index < 0)
        return dark ? CHART_PALETTE.neutral.dark : CHART_PALETTE.neutral.light;
    const colors = dark ? CHART_PALETTE.dark : CHART_PALETTE.light;
    return colors[index % colors.length] ?? colors[0] ?? '#2a78d6';
}
/**
 * The solid colour a paint stands for. A wash answers with its colour; the renderer applies the
 * opacities as a gradient.
 */
export function solidColor(paint, environment) {
    if (paint === 'ink')
        return environment.ink;
    if (paint === 'surface')
        return environment.surface;
    if (typeof paint === 'string')
        return paint;
    if (isSeries(paint))
        return seriesColor(paint.series, environment.dark);
    return solidColor(paint.wash, environment);
}
/** Relative luminance under 0.4 reads as dark. Unknown colours (a gradient URL) read as light. */
export function isDarkColor(color) {
    const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    if (match === null)
        return false;
    const hex = match[1] ?? '';
    const full = hex.length === 3 ? hex.replace(/(.)/g, '$1$1') : hex;
    const channel = (offset) => {
        const value = Number.parseInt(full.slice(offset, offset + 2), 16) / 255;
        return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
    return luminance < 0.4;
}
/**
 * Whether a chart on this fill needs a quiet panel of its own.
 *
 * The chart colours are validated against neutral surfaces — white, greys, a dark card. On a
 * saturated one (the accent root, a topic somebody coloured purple or orange) a blue column sits
 * on purple and an orange slice disappears into orange, so the chart gets a neutral card inside
 * the node instead. Very light tints stay: a pale lavender is still, for the eye, a white card.
 * A fill that is not a plain colour (a gradient) cannot be checked, and gets the panel.
 */
export function needsChartPanel(fill) {
    const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(fill.trim());
    if (match === null)
        return true;
    const hex = match[1] ?? '';
    const full = hex.length === 3 ? hex.replace(/(.)/g, '$1$1') : hex;
    const [r, g, b] = [0, 2, 4].map((offset) => Number.parseInt(full.slice(offset, offset + 2), 16) / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;
    const chroma = max - min;
    const saturation = chroma === 0 ? 0 : chroma / (1 - Math.abs(2 * lightness - 1));
    return saturation > 0.25 && lightness < 0.9;
}
/**
 * A colour at an opacity over another, as one solid colour.
 *
 * For the PDF page, which has no opacity without extra objects: a tint of ink over a white card is
 * the same pixels as the mixed colour, and that is what the page draws.
 */
export function mixHex(foreground, background, opacity) {
    const parse = (hex) => {
        const value = hex.replace('#', '');
        const full = value.length === 3 ? value.replace(/(.)/g, '$1$1') : value.slice(0, 6);
        return [0, 2, 4].map((offset) => Number.parseInt(full.slice(offset, offset + 2), 16) || 0);
    };
    const [fr, fg, fb] = parse(foreground);
    const [br, bg, bb] = parse(background);
    const mix = (f, b) => Math.round(f * opacity + b * (1 - opacity))
        .toString(16)
        .padStart(2, '0');
    return `#${mix(fr, br)}${mix(fg, bg)}${mix(fb, bb)}`;
}
export function resolvePaint(paint, environment) {
    if (isWash(paint)) {
        return {
            color: solidColor(paint.wash, environment),
            wash: { from: paint.from, to: paint.to, direction: paint.direction },
        };
    }
    return { color: solidColor(paint, environment) };
}
/**
 * A paint as one flat colour over the surface — for a page that draws neither opacity nor
 * gradients. A wash becomes its average; an opacity is mixed in.
 */
export function flatColor(paint, environment, opacity = 1) {
    const resolved = resolvePaint(paint, environment);
    const alpha = (resolved.wash === undefined ? 1 : (resolved.wash.from + resolved.wash.to) / 2) * opacity;
    return alpha >= 1 ? resolved.color : mixHex(resolved.color, environment.surface, alpha);
}
/** The environment for drawing on a node: its text colour, its fill, and whether that is dark. */
export function paintEnvironment(ink, surface) {
    return { ink, surface, dark: isDarkColor(surface) };
}
//# sourceMappingURL=paint.js.map